/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";
import { Button } from "@vector-im/compound-web";

import { FieldRow, InputField } from "../input/Input";
import { Slider } from "../Slider";
import { useSetting } from "./settings";
import {
  activeTheme,
  callLayout,
  customThemes,
  focusOutline,
  highContrast,
  largerTapTargets,
  reduceMotion,
  uiFontFamily,
  uiScale,
  type CustomTheme,
} from "../meet/MeetSettings";
import styles from "./MeetSettings.module.css";

export function applyTheme(theme: CustomTheme | null): void {
  const root = document.documentElement;
  if (!theme) {
    root.style.removeProperty("--nevai-primary");
    root.style.removeProperty("--nevai-secondary");
    root.style.removeProperty("--nevai-bg");
    root.style.removeProperty("--nevai-text");
    root.classList.remove("nevai-custom-theme");
    return;
  }
  root.style.setProperty("--nevai-primary", theme.primary);
  root.style.setProperty("--nevai-secondary", theme.secondary);
  root.style.setProperty("--nevai-bg", theme.background);
  root.style.setProperty("--nevai-text", theme.text);
  root.classList.add("nevai-custom-theme");
}

export const AppearanceSettingsTab: FC = () => {
  const [themes, setThemes] = useSetting(customThemes);
  const [active, setActive] = useSetting(activeTheme);
  const [layout, setLayout] = useSetting(callLayout);
  const [scale, setScale] = useSetting(uiScale);
  const [font, setFont] = useSetting(uiFontFamily);
  const [contrast, setContrast] = useSetting(highContrast);
  const [reduce, setReduce] = useSetting(reduceMotion);
  const [tap, setTap] = useSetting(largerTapTargets);
  const [focus, setFocus] = useSetting(focusOutline);

  const [draft, setDraft] = useState<CustomTheme>({
    name: "My Theme",
    primary: "#0dbd8b",
    secondary: "#17191c",
    background: "#101317",
    text: "#ffffff",
  });

  useEffect(() => {
    document.documentElement.style.fontSize = `${16 * scale}px`;
    document.body.style.fontFamily = font;
    document.documentElement.classList.toggle("nevai-high-contrast", contrast);
  }, [scale, font, contrast]);

  useEffect(() => {
    const t = themes.find((x) => x.name === active) ?? null;
    applyTheme(t);
  }, [active, themes]);

  return (
    <div>
      <h3 className={styles.sectionTitle}>Theme Engine</h3>
      <div className={styles.colorGrid}>
        <div className={styles.colorField}>
          <span>Primary</span>
          <input
            className={styles.colorSwatch}
            type="color"
            value={draft.primary}
            onChange={(e) => setDraft({ ...draft, primary: e.target.value })}
          />
        </div>
        <div className={styles.colorField}>
          <span>Secondary</span>
          <input
            className={styles.colorSwatch}
            type="color"
            value={draft.secondary}
            onChange={(e) => setDraft({ ...draft, secondary: e.target.value })}
          />
        </div>
        <div className={styles.colorField}>
          <span>Background</span>
          <input
            className={styles.colorSwatch}
            type="color"
            value={draft.background}
            onChange={(e) => setDraft({ ...draft, background: e.target.value })}
          />
        </div>
        <div className={styles.colorField}>
          <span>Text</span>
          <input
            className={styles.colorSwatch}
            type="color"
            value={draft.text}
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          />
        </div>
      </div>
      <FieldRow>
        <InputField
          id="themeName"
          type="text"
          label="Theme name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </FieldRow>
      <Button
        onClick={() => {
          const next = [...themes.filter((t) => t.name !== draft.name), draft];
          setThemes(next);
          setActive(draft.name);
        }}
      >
        Save & apply theme
      </Button>
      <div className={styles.themeList}>
        {themes.map((t) => (
          <button
            key={t.name}
            className={styles.themeChip}
            style={{ background: t.primary, color: t.text }}
            onClick={() => setActive(t.name)}
          >
            {t.name}
          </button>
        ))}
        {active && (
          <button className={styles.themeChip} onClick={() => setActive("")}>
            Default
          </button>
        )}
      </div>

      <h3 className={styles.sectionTitle}>Layout</h3>
      <FieldRow>
        <label htmlFor="layout">Call layout</label>
        <select
          id="layout"
          value={layout}
          onChange={(e) => setLayout(e.target.value as typeof layout)}
        >
          <option value="grid">Grid</option>
          <option value="spotlight">Spotlight</option>
          <option value="minimalist">Minimalist</option>
        </select>
      </FieldRow>

      <h3 className={styles.sectionTitle}>Font & Scaling</h3>
      <div className={styles.row}>
        <label>UI scale ({Math.round(scale * 100)}%)</label>
        <Slider
          label="UI scale"
          min={0.8}
          max={1.4}
          step={0.05}
          value={scale}
          onValueChange={setScale}
          onValueCommit={setScale}
        />
      </div>
      <FieldRow>
        <InputField
          id="font"
          type="text"
          label="Font family"
          value={font}
          onChange={(e) => setFont(e.target.value)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="contrast"
          type="checkbox"
          label="High contrast mode"
          checked={contrast}
          onChange={(e) => setContrast(e.target.checked)}
        />
      </FieldRow>

      <h3 className={styles.sectionTitle}>Accessibility</h3>
      <FieldRow>
        <InputField
          id="reduceMotion"
          type="checkbox"
          label="Reduce motion (minimise animations)"
          checked={reduce}
          onChange={(e) => setReduce(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="largeTap"
          type="checkbox"
          label="Larger tap targets (44px min hit area)"
          checked={tap}
          onChange={(e) => setTap(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="focusOutline"
          type="checkbox"
          label="Always show a visible focus ring"
          checked={focus}
          onChange={(e) => setFocus(e.target.checked)}
        />
      </FieldRow>
    </div>
  );
};
